import React, { useEffect, useRef, useState } from 'react';
import proptypes from 'prop-types';
import { FADE_DURATION_IN_MS } from './constants';
import {
  getInPlaceEnlargedImageCoordinates,
  getLensModeEnlargedImageCoordinates
} from './helpers/imageCoordinates';
import { ContainerDimensions, LargeImageShape, Point, SmallImageShape } from './propTypes';
import { getEnlargedImageContainerStyle, getEnlargedImageStyle } from './helpers/styles';

const { bool, string } = proptypes;

// Sticky column -> { panes, zIndex }: MediaGrid tiles cross-fade, so panes in one column overlap.
const liftedColumns = new WeakMap();

export const EnlargedImage = ({
  containerClassName = '',
  containerDimensions,
  cursorOffset,
  isActive = false,
  isInPlaceMode,
  isPositionOutside = false,
  largeImage,
  position = { x: 0, y: 0 },
  smallImage
}) => {
  const {
    alt = ''
  } = largeImage;

  const [isTransitionEntering, setIsTransitionEntering] = useState(false);
  const [isTransitionActive, setIsTransitionActive] = useState(false);
  const [isTransitionLeaving, setIsTransitionLeaving] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const containerRef = useRef(null);

  const timers = [];

  useEffect(() => {
    setIsMounted(true);
    return () => {
      timers.forEach((timerId) => {
        clearTimeout(timerId);
      });
    };
  }, []);

  useEffect(() => {
    if (isMounted) {
      if (isActive && !isPositionOutside) {
        setIsTransitionEntering(true);

        timers.push(setTimeout(() => {
          setIsTransitionEntering(false);
          setIsTransitionActive(true);
        }, 0));
      } else {
        setIsTransitionLeaving(true);
        setIsTransitionActive(false);

        timers.push(setTimeout(() => {
          setIsTransitionLeaving(false);
        }, FADE_DURATION_IN_MS));
      }
    }
  }, [isActive, isPositionOutside]);

  const imageCoordinates = isInPlaceMode
    ? getInPlaceEnlargedImageCoordinates({
      containerDimensions,
      largeImage,
      position
    })
    : getLensModeEnlargedImageCoordinates({
      containerDimensions,
      cursorOffset,
      largeImage,
      position,
      smallImage
    });

  const isVisible = isTransitionEntering
      || isTransitionActive
      || isTransitionLeaving;

  // Fusion sticky columns share one z-index, so a later sticky column (the buybox) would paint over a beside pane.
  useEffect(() => {
    const stickyColumn = !isInPlaceMode && isVisible && containerRef.current?.closest('.sui-sticky');
    if (!stickyColumn) return undefined;
    const lift = liftedColumns.get(stickyColumn) || { panes: 0, zIndex: stickyColumn.style.zIndex };
    if (!lift.panes) {
      stickyColumn.style.zIndex = String((parseInt(getComputedStyle(stickyColumn).zIndex, 10) || 0) + 1);
    }
    lift.panes += 1;
    liftedColumns.set(stickyColumn, lift);
    return () => {
      lift.panes -= 1;
      if (lift.panes) return;
      stickyColumn.style.zIndex = lift.zIndex;
      liftedColumns.delete(stickyColumn);
    };
  }, [isInPlaceMode, isVisible]);

  const containerStyle = getEnlargedImageContainerStyle({
    containerDimensions,
    isTransitionActive,
    isInPlaceMode
  });

  const imageStyle = getEnlargedImageStyle({
    imageCoordinates,
    largeImage
  });

  const component = (
    <div
      className={containerClassName}
      data-testid="enlarged-image"
      ref={containerRef}
      style={containerStyle}
    >
      {/* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */}
      <img
        alt={alt}
        className="sui-max-w-none"
        src={largeImage.src}
        srcSet={largeImage.srcSet}
        sizes={largeImage.sizes}
        style={imageStyle}
      />
    </div>
  );

  return isVisible ? component : null;
};

EnlargedImage.displayName = 'EnlargedImage';

EnlargedImage.propTypes = {
  containerClassName: string,
  containerDimensions: ContainerDimensions.isRequired,
  cursorOffset: Point.isRequired,
  isActive: bool,
  isInPlaceMode: bool.isRequired,
  isPositionOutside: bool,
  largeImage: LargeImageShape.isRequired,
  position: Point,
  smallImage: SmallImageShape.isRequired
};
